/**
 * The «When and where» block of the supplier's offer bar, as pure functions (SCRUM-587 S3, web;
 * `docs/V2_SHOW_MINIMAL.md` §4 and §4g). The form state, the body `POST /offers/:id/submit` takes,
 * the check before sending, the «possible days» count, and the plain sentence that says where the
 * offer went. No DOM here; the component is `components/offer-when-where.ts`.
 *
 * The block is OPTIONAL. An untouched form builds NO body, so an offer without it is sent exactly as
 * it always was. The shape of a date is `@scb/schema`'s `ShowWhen` — never redefined (rule 1).
 *
 * The supplier is told only where THEIR offer went. Nothing here can name another show or another
 * offer (owner: «пока никто не знает о конкурентах»).
 */
import { NOT_SET_ANSWER, candidateDays, type NotSetAnswer, type ShowWhen } from "@scb/schema";
import { viewerToday } from "../show-day";

export type WhenKindUi = "open" | "range" | "choice" | "day";

export interface PlacementForm {
  kind: WhenKindUi;
  /** `Day`: YYYY-MM-DD from the native date input, "" while empty. */
  day: string;
  /** `Range`. */
  from: string;
  to: string;
  /** Weekdays that WORK (0 = Sunday … 6 = Saturday). Unticked ones are taken out of the range. */
  weekdays: number[];
  /** `Choice`: specific dates, 2 or more. */
  dates: string[];
  venueText: string;
  /** Set only when the text was picked from the signed-in supplier's venue list. */
  venueId: string | null;
  city: string;
  /**
   * The supplier's ANSWER «date and venue not set yet» (SCRUM-634) — chosen on purpose in the send window, never a
   * default: an untouched form is `false`, which is «not answered», and Send stays shut. What was typed in the other
   * fields is kept while this is on.
   */
  notSet: boolean;
  /**
   * `Range`: single days taken out of the period (`except_dates`). The picker has no control for them; they come only
   * from a show that already had them, and are carried so that changing the date does not silently drop them.
   */
  exceptDates?: string[];
}

export function emptyPlacementForm(): PlacementForm {
  return { kind: "open", day: "", from: "", to: "", weekdays: [0, 1, 2, 3, 4, 5, 6], dates: [], venueText: "", venueId: null, city: "", notSet: false };
}

/** What `POST /offers/:id/submit` takes (`apps/api/src/shows/placement.ts` `OfferPlacementSchema`). */
export interface PlacementBody {
  /** A date, or `NOT_SET_ANSWER` — the explicit «not set yet» (alone: no venue travels with it). */
  when?: ShowWhen | NotSetAnswer;
  venue?: { venueId: string } | { name: string; city?: string };
  /** A show link's token (`offer/show-link.ts`): stands ALONE — the server refuses it beside `when` or `venue`. */
  showLink?: string;
}

export type PlacementErrorCode = "dayMissing" | "rangeIncomplete" | "rangeBackwards" | "rangeNoDays" | "choiceFew";

function whenOf(form: PlacementForm): ShowWhen | undefined {
  switch (form.kind) {
    case "open":
      return undefined;
    case "day":
      return form.day ? { kind: "day", date: form.day } : undefined;
    case "choice": {
      const dates = [...new Set(form.dates.filter((d) => d !== ""))].sort();
      return dates.length >= 2 ? { kind: "choice", dates } : undefined;
    }
    case "range": {
      if (!form.from || !form.to) return undefined;
      const taken = [0, 1, 2, 3, 4, 5, 6].filter((d) => !form.weekdays.includes(d));
      const skipped = [...new Set(form.exceptDates ?? [])].filter((d) => d >= form.from && d <= form.to).sort();
      return {
        kind: "window",
        from: form.from,
        to: form.to,
        ...(taken.length > 0 ? { except_weekdays: taken } : {}),
        ...(skipped.length > 0 ? { except_dates: skipped } : {}),
      };
    }
  }
}

/** Why the block cannot be sent yet — said on screen, never silently dropped. */
export function placementError(form: PlacementForm): PlacementErrorCode | undefined {
  if (form.notSet) return undefined;
  switch (form.kind) {
    case "open":
      return undefined;
    case "day":
      return form.day ? undefined : "dayMissing";
    case "choice":
      return whenOf(form) ? undefined : "choiceFew";
    case "range": {
      if (!form.from || !form.to) return "rangeIncomplete";
      if (form.to < form.from) return "rangeBackwards";
      const when = whenOf(form);
      return when && candidateDays(when).length > 0 ? undefined : "rangeNoDays";
    }
  }
}

/** «N possible days» — `undefined` while there is nothing to count. */
export function possibleDayCount(form: PlacementForm): number | undefined {
  if (form.kind === "open") return undefined;
  const when = whenOf(form);
  return when ? candidateDays(when).length : undefined;
}

/**
 * Did the person give anything at all — a date of any kind, or a venue name? (SCRUM-881: the one button
 * of the publish dialog publishes into a show when this is `true` and plainly otherwise.)
 *
 * Wider than «there is a body to send» on purpose: a period with only its first day picked yields no
 * body, but the person clearly meant a date — it must reach `placementError` and be refused out loud,
 * not be dropped while a new version goes out. A city without a venue name stays «nothing»: the show
 * form itself sends no venue for it.
 */
export function placementFilled(form: PlacementForm): boolean {
  return form.kind !== "open" || form.venueText.trim() !== "";
}

/** A copy that shares no array with the original — what the publish dialog hands on after it closes. */
export function clonePlacementForm(form: PlacementForm): PlacementForm {
  return { ...form, weekdays: [...form.weekdays], dates: [...form.dates] };
}

/** The body to send, or `undefined` when the block is empty (the offer goes as today). A guest has no
 *  venues of their own, so only text is sent for them. */
export function buildPlacementBody(form: PlacementForm, ctx: { signedIn: boolean }): PlacementBody | undefined {
  if (form.notSet) return { when: NOT_SET_ANSWER };
  const body: PlacementBody = {};
  const when = whenOf(form);
  if (when) body.when = when;
  const name = form.venueText.trim();
  if (name !== "") {
    const city = form.city.trim();
    body.venue = form.venueId && ctx.signedIn ? { venueId: form.venueId } : { name, ...(city !== "" ? { city } : {}) };
  }
  return body.when || body.venue ? body : undefined;
}

// ---------------------------------------------------------------------------------------------
// Quick chips
// ---------------------------------------------------------------------------------------------

export type RangeChip = "thisWeek" | "nextWeek" | "lastWeekOfMonth" | "wholeMonth";

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Today as YYYY-MM-DD on the viewer's own calendar (the browser's zone: `show-day.ts`, the one place for «today»). */
export function todayIso(now: Date = new Date()): string {
  return viewerToday(now);
}

function monthEnd(year: number, month0: number): string {
  return new Date(Date.UTC(year, month0 + 1, 0)).toISOString().slice(0, 10);
}

/** The month the two month chips talk about: this month, or the next once its last week has begun. */
export function chipMonth(today: string): { year: number; month0: number } {
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  const lastDay = Number(monthEnd(y, m - 1).slice(8));
  return d > lastDay - 6 ? (m === 12 ? { year: y + 1, month0: 0 } : { year: y, month0: m }) : { year: y, month0: m - 1 };
}

export function rangeForChip(chip: RangeChip, today: string): { from: string; to: string } {
  if (chip === "thisWeek" || chip === "nextWeek") {
    const dow = new Date(`${today}T00:00:00Z`).getUTCDay();
    const monday = addDays(today, -((dow + 6) % 7) + (chip === "nextWeek" ? 7 : 0));
    return { from: monday, to: addDays(monday, 6) };
  }
  const { year, month0 } = chipMonth(today);
  const end = monthEnd(year, month0);
  return chip === "lastWeekOfMonth" ? { from: addDays(end, -6), to: end } : { from: `${end.slice(0, 8)}01`, to: end };
}

/**
 * What a chip puts in the form: its range, from no earlier than `firstPickable` (the first day the picker has not switched off - the
 * show's zone can be a day ahead of the viewer's). `undefined` when no day of it is left (This week, on a Sunday evening, with the venue's
 * zone already on Monday): the chip then has nothing to give and is switched off, as the picker switches off a day that is over.
 */
export function chipRange(chip: RangeChip, today: string, firstPickable: string): { from: string; to: string } | undefined {
  const r = rangeForChip(chip, today);
  const from = r.from < firstPickable ? firstPickable : r.from;
  return from > r.to ? undefined : { from, to: r.to };
}

// ---------------------------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------------------------

export function intlLocale(locale: string): string {
  return locale === "en" ? "en-GB" : locale;
}

function fmtDay(iso: string, locale: string, withWeekday: boolean): string {
  const text = new Intl.DateTimeFormat(intlLocale(locale), {
    ...(withWeekday ? { weekday: "short" as const } : {}),
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${iso}T00:00:00Z`));
  return text.replace(",", "");
}

/** A short name for a weekday (0 = Sunday), in the reader's language. */
export function weekdayName(day: number, locale: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { weekday: "short", timeZone: "UTC" }).format(new Date(Date.UTC(2023, 0, 1 + day)));
}

export function monthName(month0: number, locale: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2026, month0, 1)));
}

export function formatDay(iso: string, locale: string): string {
  return fmtDay(iso, locale, true);
}

function remainingWeekdays(taken: readonly number[]): number[] {
  return [0, 1, 2, 3, 4, 5, 6].filter((d) => !taken.includes(d));
}

/** «Wed–Sat» for an unbroken run of three or more days, otherwise the days listed: «Sun, Fri, Sat». */
function sayWeekdays(days: readonly number[], locale: string): string {
  const unbroken = days.length >= 3 && days.every((d, i) => i === 0 || d === days[i - 1]! + 1);
  if (unbroken) return `${weekdayName(days[0]!, locale)}–${weekdayName(days[days.length - 1]!, locale)}`;
  return days.map((d) => weekdayName(d, locale)).join(", ");
}

/** A date as a person says it; `undefined` for «not yet». A range names the weekdays that remain. */
export function describeWhen(when: ShowWhen, locale: string): string | undefined {
  switch (when.kind) {
    case "open":
      return undefined;
    case "day":
      return fmtDay(when.date, locale, true);
    case "window": {
      const range = `${fmtDay(when.from, locale, false)} – ${fmtDay(when.to, locale, false)}`;
      const days = remainingWeekdays(when.except_weekdays ?? []);
      return days.length === 7 ? range : `${range}, ${sayWeekdays(days, locale)}`;
    }
    case "choice":
      return [...when.dates].sort().map((d) => fmtDay(d, locale, true)).join(" / ");
  }
}

export interface PlacedAttach {
  showId: string;
  how: string;
}

export interface PlacedOffered {
  when: unknown;
  venue: { name: string; city?: string | null } | null;
}

type Say = (key: "offer.placed.whenVenue" | "offer.placed.when" | "offer.placed.venue" | "offer.placed.none", vars: Record<string, string>) => string;

/** «Sent — it is in the band's show for Sat 14 Nov, Harbour Hall.» — only when the offer was placed,
 *  and only about the supplier's own date and venue. */
export function placedSentence(
  attach: PlacedAttach | null | undefined,
  offered: PlacedOffered | null | undefined,
  locale: string,
  say: Say
): string | undefined {
  if (!attach) return undefined;
  const raw = offered?.when as ShowWhen | undefined;
  const when = raw && typeof raw === "object" && "kind" in raw ? describeWhen(raw, locale) : undefined;
  const venue = offered?.venue?.name;
  if (when && venue) return say("offer.placed.whenVenue", { when, venue });
  if (when) return say("offer.placed.when", { when });
  if (venue) return say("offer.placed.venue", { venue });
  return say("offer.placed.none", {});
}

// ---------------------------------------------------------------------------------------------
// Kept for the sign-in round trip (a guest presses Send, signs in, the offer goes by itself)
// ---------------------------------------------------------------------------------------------

const KEY_PREFIX = "scb.offerPlacement:";

/** The same 48 hours as the kept answers (`guest-offer-draft.ts`) and the link in the letter. */
export const PLACEMENT_TTL_MS = 48 * 3_600_000;

function keyFor(riderId: string, version: number): string {
  return `${KEY_PREFIX}${riderId}:${version}`;
}

function isForm(x: unknown): x is PlacementForm {
  if (typeof x !== "object" || x === null) return false;
  const f = x as Record<string, unknown>;
  const strings = ["day", "from", "to", "venueText", "city"].every((k) => typeof f[k] === "string");
  const lists = Array.isArray(f.weekdays) && f.weekdays.every((n) => typeof n === "number") && Array.isArray(f.dates) && f.dates.every((s) => typeof s === "string");
  // `notSet` was added later: a form kept before it existed has none, and that means «not answered».
  const notSet = f.notSet === undefined || typeof f.notSet === "boolean";
  return (
    strings && lists && notSet && (f.kind === "open" || f.kind === "range" || f.kind === "choice" || f.kind === "day") && (f.venueId === null || typeof f.venueId === "string")
  );
}

export function writePlacementForm(riderId: string, version: number, form: PlacementForm): void {
  try {
    if (buildPlacementBody(form, { signedIn: true }) === undefined && form.kind === "open") {
      localStorage.removeItem(keyFor(riderId, version));
      return;
    }
    localStorage.setItem(keyFor(riderId, version), JSON.stringify({ savedAt: Date.now(), form }));
  } catch {
    /* only the memory of the form is lost; the offer can still be sent */
  }
}

/**
 * A kept form (the sign-in round trip, 48 hours) must not bring back a day that is already over: owner, 30.10.2026, past dates are not
 * for a show. `minDay` is the first day that is not over anywhere; the picker and the server judge the rest in the show's own zone.
 * A day, a period that starts before it, and the past dates of a choice are taken out; the venue and everything else stay as typed.
 */
export function dropPastDays(form: PlacementForm, minDay: string): PlacementForm {
  const out: PlacementForm = { ...form, weekdays: [...form.weekdays], dates: form.dates.filter((d) => d >= minDay) };
  if (out.day !== "" && out.day < minDay) out.day = "";
  if (out.from !== "" && out.from < minDay) {
    out.from = "";
    out.to = "";
  }
  return out;
}

export function readPlacementForm(riderId: string, version: number): PlacementForm | undefined {
  try {
    const raw = localStorage.getItem(keyFor(riderId, version));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { savedAt?: unknown; form?: unknown };
    if (typeof parsed.savedAt !== "number" || Date.now() - parsed.savedAt > PLACEMENT_TTL_MS) {
      clearPlacementForm(riderId, version);
      return undefined;
    }
    return isForm(parsed.form) ? { ...parsed.form, notSet: parsed.form.notSet === true } : undefined;
  } catch {
    return undefined;
  }
}

export function clearPlacementForm(riderId: string, version: number): void {
  try {
    localStorage.removeItem(keyFor(riderId, version));
  } catch {
    /* nothing to clean up if storage was never reachable */
  }
}
